import {ChangeDetectorRef, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {DataSource, CollectionViewer} from '@angular/cdk/collections';
import {PageEvent} from '@angular/material/paginator';
import {Sort, SortDirection} from '@angular/material/sort';

export interface ListResponse<T> {
  items: T[];
  total: number;
}

export interface ListRequest {
  page: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  filters?: { [key: string]: any };
}

export interface QuerySort {
  [key: string]: SortDirection;
}

export interface SimplePagination {
  page: number;
  limit: number;
}

export class TableError extends Error {
  constructor(message: string, public operation: string, public originalError?: any) {
    super(message);
    this.name = 'TableError';
  }
}
@Injectable({
  providedIn: 'root'
})
export class CustomListDataSourceService<T> implements DataSource<T> {
  protected _data$: BehaviorSubject<T[]>;
  protected _destroy$ = new Subject<void>();
  protected _sort$ = new BehaviorSubject<QuerySort>({});
  protected _pageChanged$ = new BehaviorSubject<SimplePagination>({ page: 1, limit: 10 });
  protected _clickedRows = new Set<T>();
  protected _error$ = new BehaviorSubject<TableError | null>(null);
  protected _loading$ = new BehaviorSubject<boolean>(false);
  
  public pageSizeOptions = [1, 10, 25, 50, 100];
  public pageSize = 10;
  public pageIndex = 0;
  public totalItems = 0;

  public refresh$ = new BehaviorSubject<null>(null);

  // Public observables for components to subscribe to
  public error$ = this._error$.asObservable();
  public loading$ = this._loading$.asObservable();

  constructor() {
    this._data$ = new BehaviorSubject<T[]>([]);
  }

  public connect(collectionViewer: CollectionViewer): Observable<T[] | readonly T[]> {
    return this._data$;
  };

  public disconnect(collectionViewer: CollectionViewer): void {
    this._destroy$.next();
  };

  public sortChanged(sort: Sort) {
    if (!sort.active) return;

    const currentSort = this._sort$.getValue();

    if (sort.direction === '' && currentSort[sort.active]) {
      delete currentSort[sort.active];
    } else {
      currentSort[sort.active] = sort.direction;
    }

    this._sort$.next(currentSort);
  };

  public pageChanged(page: PageEvent) {
    const pagination: SimplePagination = { page: page.pageIndex + 1, limit: page.pageSize }
    this._pageChanged$.next(pagination);
  }

  public getSortQuery(sort: QuerySort) {
    let sortQuery = ''
    Object.entries(sort).map(([key, direction], value) => {
      sortQuery += `&sort=${key}.${direction}`;
    });
    return sortQuery;
  }

  onRowClick(row: T) {
    // Single-select behavior: clear all previous selections first
    this._clickedRows.clear();
    
    // Add only the clicked row
    this._clickedRows.add(row);
  }

  removeSelection(row: T) {
    this._clickedRows.delete(row);
  }

  addSelection(row: T) {
    this._clickedRows.add(row);
  }

  clearSelection() {
    this._clickedRows.clear();
  }

  isSelected(row: T): boolean {
    return this._clickedRows.has(row);
  }

  // Error handling methods
  protected handleError(operation: string, error: any): void {
    const tableError = new TableError(
      `Failed to ${operation}`, 
      operation, 
      error
    );
    this._error$.next(tableError);
    this._loading$.next(false);
  }

  protected setLoading(loading: boolean): void {
    this._loading$.next(loading);
  }

  protected clearError(): void {
    this._error$.next(null);
  }

  // Safe localStorage operations
  public safeLocalStorageGet(key: string): any {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      }
    } catch (error) {
      console.warn(`Failed to load from localStorage key: ${key}`, error);
    }
    return null;
  }

  public safeLocalStorageSet(key: string, value: any): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      console.warn(`Failed to save to localStorage key: ${key}`, error);
    }
  }

  refresh() {
    this.refresh$.next(null);
  }
}
